import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Box, Chip, Typography, Alert, Divider, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import DocumentationSidebar from '../../../components/Documentatin/DocumentationSidebar';
import DocumentationTabs from '../../../components/Documentatin/DocumentationTabs';
import ContextFilterAndSort from '../../../components/ContextFilterAndSort/ContextFilterAndSort';
import DocumentationTable from '../../../components/Documentatin/DocumentationTable';
import PageTitle from '../../../components/PageTitle/PageTitle';
import { getCategoryCounts, getDocumentsMetaData, getFavorites, getRecentlyViewed } from '../../../services/documentService';
import { useSite } from '../../../context/siteContext';
import { getRelativeTime } from '../../../utilities/dateUtils';
import CloseIcon from '@mui/icons-material/Close';
import './Documentation.css';
import { useAuth } from '../../../context/authContext';
import DataGridSkeletonForDocumentation from '../../../components/PageSkeletons/DataGridSkeletonForDocumentation';
import { toast } from '../../../context/ToastContext';
import {
    DOCUMENT_TAB_CATEGORIES,
    KNOWN_DOCUMENT_CATEGORIES,
    normalizeDocumentCategoryKey,
} from '../../../utils/documentCategories';
import { trackEvent } from '../../../utilities/analyticsService';

const buildCloneSafeDocumentationState = (state) => {
    if (!state) return undefined;

    const machine = state.machine || state.selectedMachine;
    const safeState = {};

    if (state.fromMachineCard === true) safeState.fromMachineCard = true;
    if (state.fromMachinePage === true) safeState.fromMachinePage = true;
    if (state.fromSearchResults === true) safeState.fromSearchResults = true;
    if (state.fromNotification === true) safeState.fromNotification = true;
    if (state.highlightCategory === true) safeState.highlightCategory = true;
    if (state.searchTerm) safeState.searchTerm = String(state.searchTerm);
    if (state.machineNumber) safeState.machineNumber = String(state.machineNumber);
    if (state.siteId !== undefined && state.siteId !== null) safeState.siteId = String(state.siteId);

    if (machine?.machine_id || machine?.machine_number) {
        safeState.machine = {
            machine_id: machine.machine_id || machine.machine_number,
            machine_name: machine.machine_name || '',
            machine_number: machine.machine_number || machine.machine_id || '',
        };
    }

    return Object.keys(safeState).length ? safeState : undefined;
};

const Documentation = ({
    hideSidebar = false,
    adminLocation = null,
    adminMachine = "all",
    isAdminView = false,
    filterdSites = [],
    searchBysiteIds = false,
    customerSiteIds = []
}) => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { machineId: machineIdFromPath } = useParams();
    const cloneSafeLocationState = useMemo(
        () => buildCloneSafeDocumentationState(location.state),
        [location.state]
    );

    const [searchParams, setSearchParams] = useSearchParams();
    const viewParam = searchParams.get('view');

    const { selectedSite, siteData } = useSite();
    const { user, hasPermission } = useAuth();
    const isSuperAdmin = Number(user?.user_id) === 1;
    const hasDocumentPermission = useCallback((permission, aliases = []) => {
        if (isSuperAdmin) return true;
        return [permission, ...aliases].some((key) =>
            hasPermission(`document_management.${key}`)
        );
    }, [hasPermission, isSuperAdmin]);
    const canAddDocument =
        isAdminView && hasDocumentPermission("create_documents");
    const canUpdateDocumentCategories =
        isAdminView && hasDocumentPermission("update_document_category", ["edit_documents"]);
    const canChangeDocumentMachine =
        isAdminView && hasDocumentPermission("change_document_machine", ["edit_documents"]);
    const canDeleteDocuments =
        isAdminView && hasDocumentPermission("delete_documents");
    const canDownloadSelectedDocuments =
        !isAdminView || hasDocumentPermission("download_documents");

    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [serverCategoryCounts, setServerCategoryCounts] = useState({});
    const [countsLoading, setCountsLoading] = useState(false);
    const lastCountsKeyRef = useRef({ siteId: null, machineId: null, searchTerm: '' });
    const inFlightDocumentsRequestRef = useRef(null);
    const lastTrackedSearchRef = useRef("");

    const initialMachineFromNavRef = useRef(null);
    const lastAppliedMachineRef = useRef(null);
    const isSiteChangingRef = useRef(false);

    // ─── NEW: separate loading states so tab/search changes don't full-remount ───
    const [initialLoading, setInitialLoading] = useState(true);   // Only true on very first load
    const [tableLoading, setTableLoading] = useState(false);       // True only when table data is fetching

    const selectedCategory = searchParams.get('category') || 'manual';


    const [searchTerm, setSearchTerm] = useState('');
    const lastSearchParamRef = useRef(null);
    const [sortOption, setSortOption] = useState(t('DOCUMENTATION.SORT_NONE'));
    const machineParam = searchParams.get('machine');
    const [selectedMachine, setSelectedMachine] = useState(() => {
        const machineFromState = location.state?.machine;

        if (machineFromState?.machine_id || machineFromState?.machine_number) {
            return {
                machine_id: machineFromState.machine_id || machineFromState.machine_number,
                machine_name: machineFromState.machine_name || '',
                machine_number: machineFromState.machine_number || machineFromState.machine_id || ''
            };
        }

        const machineFromUrl = machineParam || machineIdFromPath;
        if (machineFromUrl) {
            return {
                machine_id: machineFromUrl,
                machine_name: '',
                machine_number: machineFromUrl
            };
        }

        return {
            machine_id: 'ALL',
            machine_name: '',
            machine_number: ''
        };
    });
    const [documents, setDocuments] = useState([]);
    const [error, setError] = useState(null);
    const [showOnlyFavorites, setShowOnlyFavorites] = useState(searchParams.get('favorites') === 'true');
    const [showOnlyRecent, setShowOnlyRecent] = useState(searchParams.get('recent') === 'true' || viewParam === 'recent');
    const [favorites, setFavorites] = useState([]);
    const [recentDocuments, setRecentDocuments] = useState([]);
    const [selectedDocuments, setSelectedDocuments] = useState([]);
    const [highlightRecent, setHighlightRecent] = useState(false);
    const [highlightMachine, setHighlightMachine] = useState(false);
    const [favoriteDocumentsWithTimestamps, setFavoriteDocumentsWithTimestamps] = useState([]);
    const [adminAllSitesFavoriteIds, setAdminAllSitesFavoriteIds] = useState([]);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    const [isManualTabSelection, setIsManualTabSelection] = useState(false);
    const [sortConfig] = useState({ sortBy: 'uploaded_at', sortOrder: 'DESC' });
    const [isFromMachineCard, setIsFromMachineCard] = useState(false);

    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [totalRows, setTotalRows] = useState(0);
    const [refreshNonce, setRefreshNonce] = useState(0);

    const getMachineIdForApi = useCallback((machine) => {
        if (!machine) return null;
        const id = machine.machine_id;
        if (!id || String(id).toUpperCase() === 'ALL') return null;
        const idStr = String(id);

        if (machineIdFromPath && idStr === String(machineIdFromPath) && /^\d+$/.test(idStr)) {
            return id;
        }

        if (!machine.machine_name) return null;
        if (idStr.includes('-')) return null;
        if (machine.machine_number && idStr === String(machine.machine_number)) return null;
        if (!/^\d+$/.test(idStr)) return null;
        return id;
    }, [machineIdFromPath]);

    /**
     * The machine filter to send to the documents API, resolved one way for every fetch.
     *
     * The two views take it from different places: admin drives the machine from its own filter
     * bar (`adminMachine`) and pins `selectedMachine` to 'ALL', for which getMachineIdForApi
     * returns null, while the customer view has no admin bar and drives it from
     * `selectedMachine`. That choice has to be made somewhere, and it is made HERE rather than
     * at each call site because the call sites drifted: the filter effect honoured
     * `adminMachine` but the pagination handler did not, so in admin view page 1 was filtered by
     * the chosen machine and page 2 silently dropped the filter.
     */
    const machineIdForApi = useMemo(
        () => (isAdminView ? adminMachine : getMachineIdForApi(selectedMachine)),
        [isAdminView, adminMachine, getMachineIdForApi, selectedMachine]
    );

    const getCategoryTabKey = useCallback((category) => {
        const normalized = normalizeDocumentCategoryKey(
            category?.category_name || category?.name || category || ''
        );
        const supportedTabs = new Set(DOCUMENT_TAB_CATEGORIES);

        if (!normalized) return 'manual';
        return supportedTabs.has(normalized) ? normalized : 'others';
    }, []);

    const normalizeVisibleDocument = useCallback((doc) => {
        if (!doc) return null;

        const normalizedMachineDetails = Array.isArray(doc.machine_details)
            ? doc.machine_details
            : [];

        const normalizedAssignedMachines = normalizedMachineDetails.length > 0
            ? Array.from(
                new Set(
                    normalizedMachineDetails
                        .map(machine => machine?.machine_id)
                        .filter(machineId => machineId !== undefined && machineId !== null)
                        .map(machineId => String(machineId))
                )
            )
            : (Array.isArray(doc.assigned_machines)
                ? doc.assigned_machines.map(id => String(id))
                : []);

        return {
            ...doc,
            machine_details: normalizedMachineDetails,
            assigned_machines: normalizedAssignedMachines,
        };
    }, []);

    // ─── MERGED from old: debug log for user role ─────────────────────────────
    useEffect(() => {

    }, [user?.role]);

    // ─── Debounce search ──────────────────────────────────────────────────────
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Seed search term from navigation (Search Results -> Documentation)
    useEffect(() => {
        const searchParamRaw =
            searchParams.get('q') ||
            searchParams.get('search') ||
            location.state?.searchTerm ||
            '';
        const searchParam = String(searchParamRaw).trim();
        if (!searchParam) return;
        if (lastSearchParamRef.current === searchParam) return;
        lastSearchParamRef.current = searchParam;
        setSearchTerm(searchParam);
    }, [searchParams, location.state]);

    // ─── On mount: check fromMachineCard ─────────────────────────────────────
    useEffect(() => {
        const fromNav =
            location.state?.fromMachineCard === true ||
            new URLSearchParams(location.search).get('fromMachine') === 'true';
        if (fromNav) setIsFromMachineCard(true);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Sync selectedCategory when URL changes ──────────────────────────────
    useEffect(() => {

    }, [selectedCategory]);
    // ─── Ensure category is always set (default to manual) ──────────────────
    useEffect(() => {
        if (!searchParams.get('category')) {
            // FIX: use functional form to preserve machine= and other params
            setSearchParams(prev => {
                const next = new URLSearchParams(prev);
                next.set('category', 'manual');
                return next;
            }, { replace: true });
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const sortOptions = useMemo(() => [
        t('DOCUMENTATION.SORT_NONE'),
        t('DOCUMENTATION.SORT_FILE_NAME_ASC'),
        t('DOCUMENTATION.SORT_FILE_NAME_DESC'),
        t('DOCUMENTATION.SORT_SERIAL_NUMBER_ASC'),
        t('DOCUMENTATION.SORT_SERIAL_NUMBER_DESC'),
        t('DOCUMENTATION.SORT_DATE_ASC'),
        t('DOCUMENTATION.SORT_DATE_DESC'),
    ], [t]);

    const selectedSiteRecord = useMemo(
        () => siteData.find((site) => site.customer_site_number === selectedSite) || null,
        [siteData, selectedSite]
    );

    const selectedSiteId = selectedSiteRecord?.site_id;
    const selectedSiteNumber = selectedSiteRecord?.customer_site_number;
    const machineSetByNavigationRef = useRef(false);

    const effectiveSiteId = useMemo(() => {
        if (isAdminView && !adminLocation) return null;
        return adminLocation || selectedSiteId;
    }, [isAdminView, adminLocation, selectedSiteId]);

    const effectiveSiteNumber = useMemo(() => {
        if (isAdminView && !adminLocation) return null;
        if (adminLocation) {
            const adminSite = siteData.find(site => site.site_id === adminLocation);
            return adminSite?.customer_site_number || null;
        }
        return selectedSiteNumber;
    }, [isAdminView, adminLocation, selectedSiteNumber, siteData]);

    // Prefer explicit customer-scoped site IDs when "All locations" is selected in admin filters
    const adminSiteIds = useMemo(() => {
        if (searchBysiteIds && Array.isArray(customerSiteIds) && customerSiteIds.length > 0) {
            return customerSiteIds;
        }
        return (filterdSites || [])
            .map(site => site.value)
            .filter(v => v !== undefined && v !== null);
    }, [searchBysiteIds, customerSiteIds, filterdSites]);

    const adminSiteIdsKey = useMemo(
        () => adminSiteIds.map(String).sort().join(","),
        [adminSiteIds]
    );

    // ─── Reset server counts when site changes ────────────────────────────────
    useEffect(() => {
        setServerCategoryCounts({});
    }, [effectiveSiteId]);

    // ─── Load category counts (independent of document fetch) ────────────────
    useEffect(() => {
        if (!effectiveSiteId && !isAdminView) return;

        let cancelled = false;
        const loadCategoryCounts = async () => {
            try {
                setCountsLoading(true);
                const effectiveMachineIdForCounts = isAdminView ? adminMachine : selectedMachine?.machine_id;
                const siteIds = adminSiteIds;

                const response = await getCategoryCounts(effectiveSiteId, {
                    machineId: machineIdForApi,
                    searchTerm: debouncedSearchTerm,
                    siteIds: siteIds,
                    searchBysiteIds: searchBysiteIds
                });
                if (!cancelled && response.status) {
                    setServerCategoryCounts(response.data);
                    lastCountsKeyRef.current = {
                        siteId: effectiveSiteId ?? null,
                        machineId: effectiveMachineIdForCounts ?? null,
                        searchTerm: (debouncedSearchTerm || '').trim(),
                    };
                }
            } catch (err) {
                if (!cancelled) console.error('Error loading category counts:', err);
            } finally {
                if (!cancelled) setCountsLoading(false);
            }
        };

        loadCategoryCounts();
        return () => { cancelled = true; };
    }, [
        effectiveSiteId,
        isAdminView,
        selectedMachine?.machine_id,
        debouncedSearchTerm,
        adminMachine,
        machineIdForApi,
        adminSiteIdsKey,
        searchBysiteIds,
        refreshNonce,
    ]);

    // ─── Core document fetcher ────────────────────────────────────────────────
    // Uses tableLoading so the skeleton never re-mounts on tab/search/filter changes
    const fetchDocuments = useCallback(async (
        page = 0,
        pageSize = 10,
        category = null,
        machineId = null,
        searchTermValue = ''
    ) => {


        if (!effectiveSiteId && !isAdminView) {

            setDocuments([]);
            return;
        }

        setTableLoading(true);
        setError(null);
        const siteIds = adminSiteIds;
        const requestKey = JSON.stringify({
            siteId: effectiveSiteId,
            isAdminView,
            page,
            pageSize,
            category: category || null,
            machineId: machineId || null,
            searchTerm: (searchTermValue || '').trim(),
            siteIds,
            searchBysiteIds,
            refreshNonce,
        });

        if (inFlightDocumentsRequestRef.current === requestKey) {
            return;
        }
        inFlightDocumentsRequestRef.current = requestKey;
        try {

            const response = await getDocumentsMetaData(effectiveSiteId, {
                page: page + 1,
                limit: pageSize,
                sortBy: sortConfig.sortBy,
                sortOrder: sortConfig.sortOrder,
                category: category,
                machineId: machineId,
                searchTerm: searchTermValue,
                siteIds: siteIds,
                searchBysiteIds: searchBysiteIds
            });

            if (response.status && Array.isArray(response.data)) {
                const processedDocs = response.data
                    .map(normalizeVisibleDocument)
                    .filter(Boolean);

                setDocuments(processedDocs);

                if (response.pagination) {
                    setTotalRows(response.pagination.total_items);
                }
                const trimmedSearchTerm = String(searchTermValue || "").trim();

                if (trimmedSearchTerm) {
                    const resultCount = Number(response.pagination?.total_items ?? processedDocs.length) || 0;
                    const trackingKey = [
                        trimmedSearchTerm.toLowerCase(),
                        effectiveSiteId || "",
                        category || "",
                        machineId || "",
                        isAdminView ? "admin_documentation" : "documentation",
                        resultCount,
                    ].join("|");

                    if (lastTrackedSearchRef.current !== trackingKey) {
                        lastTrackedSearchRef.current = trackingKey;

                        trackEvent("search_performed", {
                            search_term: trimmedSearchTerm,
                            search_source: isAdminView ? "admin_documentation" : "documentation",
                            page_name: isAdminView ? "Admin Documentation" : "Documentation",
                            page_path: `${location.pathname}${location.search || ""}`,
                            result_count: resultCount,
                            has_results: resultCount > 0,
                            no_results: resultCount === 0,
                            category: category || "",
                            machine_id: machineId || "",
                            site_id: effectiveSiteId || "",
                            site_number: effectiveSiteNumber || "",
                        });
                    }
                }
            } else if (!response.status) {
                console.warn('API returned non-success status, clearing documents silently');
                setDocuments([]);
                setTotalRows(0);
            } else {
                console.error('Unexpected response format:', response);
                setDocuments([]);
                setTotalRows(0);
            }
        } catch (err) {
            console.error('Error fetching documents:', err);
            setError(err.message || 'Failed to fetch documents');
            setDocuments([]);
        } finally {
            if (inFlightDocumentsRequestRef.current === requestKey) {
                inFlightDocumentsRequestRef.current = null;
            }
            setTableLoading(false);
        }
    }, [
        effectiveSiteId,
        effectiveSiteNumber,
        isAdminView,
        sortConfig,
        adminSiteIdsKey,
        searchBysiteIds,
        refreshNonce,
        normalizeVisibleDocument,
        location.pathname,
        location.search,
    ]);

    useEffect(() => {



        // Exit if no machine identifier at all
        if (!selectedMachine?.machine_id && !selectedMachine?.machine_number) {

            return;
        }

        // Exit only if FULLY enriched (has all three: id, name, number)
        if (selectedMachine.machine_id && selectedMachine.machine_name && selectedMachine.machine_number) {

            return;
        }



        // 1. Try from documents

        const machineFromDocs = documents
            .flatMap(doc => doc.machine_details || [])
            .find(m =>
                String(m.machine_id) === String(selectedMachine.machine_id) ||
                String(m.machine_number) === String(selectedMachine.machine_number)
            );

        if (machineFromDocs) {

            const enriched = {
                machine_id: machineFromDocs.machine_id,
                machine_name: machineFromDocs.machine_name,
                machine_number: machineFromDocs.machine_number
            };

            setSelectedMachine(enriched);
            lastAppliedMachineRef.current = enriched;
            return;
        }

        // 2. Try from nav state ref (works even when documents=[])

        const ref = machineFromNavStateRef.current;
        if (ref?.machine_name && (
            String(ref.machine_id) === String(selectedMachine.machine_id) ||
            String(ref.machine_number) === String(selectedMachine.machine_number)
        )) {


            setSelectedMachine(ref);
            lastAppliedMachineRef.current = ref;
            return;
        }




    }, [documents, selectedMachine]);

    // ─── Fetch favorites ──────────────────────────────────────────────────────
    const fetchFavorites = useCallback(async () => {
        if (!effectiveSiteId && isAdminView) return;
        // MERGED from old: also guard when still in initial loading state
        if (!effectiveSiteId) return;
        try {
            const response = await getFavorites(effectiveSiteId);
            if (response.status && Array.isArray(response.data)) {
                const visibleFavorites = response.data
                    .map(normalizeVisibleDocument)
                    .filter(Boolean);
                setFavoriteDocumentsWithTimestamps(visibleFavorites);
                setFavorites(visibleFavorites.map(doc => doc.id));
            }
        } catch (err) {
            console.error('Error fetching favorites:', err);
        }
    }, [effectiveSiteId, isAdminView, normalizeVisibleDocument]);

    // ─── Fetch recently viewed ────────────────────────────────────────────────
    const fetchRecentlyViewed = useCallback(async () => {
        if (!effectiveSiteId && isAdminView) return;
        if (!effectiveSiteId) return;
        try {
            const response = await getRecentlyViewed(50, effectiveSiteId);
            if (response.status && Array.isArray(response.data)) {
                const recentDocs = response.data
                    .map(normalizeVisibleDocument)
                    .filter(Boolean)
                    .map(doc => ({
                        ...doc,
                        isRecentlyViewed: true,
                        viewedTime: getRelativeTime(doc.viewed_at),
                    }));
                setRecentDocuments(recentDocs);
                setDocuments(prev =>
                    prev.map(doc => {
                        const recentDoc = recentDocs.find(r => r.id === doc.id);
                        return recentDoc
                            ? { ...doc, isRecentlyViewed: true, viewedTime: recentDoc.viewedTime, viewed_at: recentDoc.viewed_at, view_count: recentDoc.view_count }
                            : { ...doc, isRecentlyViewed: false };
                    })
                );
            }
        } catch (err) {
            console.error('Error fetching recently viewed:', err);
        }
    }, [effectiveSiteId, isAdminView]);

    // ─── INITIAL LOAD: runs once when site is ready ───────────────────────────
    const hasInitialLoadRun = useRef(false);
    useEffect(() => {
        if (!effectiveSiteId && !isAdminView) return;
        if (hasInitialLoadRun.current) return;
        hasInitialLoadRun.current = true;

        const doInitialLoad = async () => {
            setInitialLoading(true);
            await fetchDocuments(0, paginationModel.pageSize, selectedCategory, machineIdForApi, debouncedSearchTerm);
            await fetchFavorites();
            await fetchRecentlyViewed();
            setInitialLoadComplete(true);
            setInitialLoading(false);
        };

        doInitialLoad();
    }, [effectiveSiteId, isAdminView]); // eslint-disable-line react-hooks/exhaustive-deps

    // ─── FILTER CHANGE: runs when tab/search/machine changes (NOT on first load) ──
    const isFirstFilterRun = useRef(true);
    useEffect(() => {
        if (isSiteChangingRef.current) return; // ← FIX 2: skip if site change is in progress
        if (!effectiveSiteId && !isAdminView) return;

        setPaginationModel(prev => ({ ...prev, page: 0 }));

        const categoryToSend =
            (showOnlyFavorites || showOnlyRecent)
                ? null
                : selectedCategory;


        if (showOnlyFavorites || showOnlyRecent) {
            return;
        }
        fetchDocuments(0, paginationModel.pageSize, categoryToSend, machineIdForApi, debouncedSearchTerm);
    }, [
        selectedCategory,
        machineIdForApi,
        debouncedSearchTerm,
        showOnlyFavorites,
        showOnlyRecent,
        effectiveSiteId,
        isAdminView,
        fetchDocuments,
        paginationModel.pageSize,
        refreshNonce,
    ]);

    // ─── Re-fetch when site changes (reset everything) ────────────────────────
    const prevSiteIdRef = useRef(effectiveSiteId);
    useEffect(() => {
        if (prevSiteIdRef.current === effectiveSiteId) return;

        const previousSiteId = prevSiteIdRef.current;
        prevSiteIdRef.current = effectiveSiteId;

        if (!effectiveSiteId && !isAdminView) return;

        const isActiveSiteSwitch = !!previousSiteId;

        if (isActiveSiteSwitch) {
            isSiteChangingRef.current = true; // ← FIX 2: block filter effect

            setShowOnlyFavorites(false);
            setShowOnlyRecent(false);
            setHighlightRecent(false);
            setInitialLoading(true); // ← set FIRST so skeleton mounts, DataGrid unmounts

            setSearchParams(prev => {
                const next = new URLSearchParams(prev);
                next.delete('favorites');
                next.delete('recent');
                next.delete('view');
                return next;
            }, { replace: true });

            setSelectedMachine({ machine_id: 'ALL', machine_name: '', machine_number: '' });
            lastAppliedMachineRef.current = null;

            setSearchParams(prev => {
                const next = new URLSearchParams(prev);
                next.delete('machine');
                next.set('category', 'manual');
                return next;
            }, { replace: true });
        }

        hasInitialLoadRun.current = false;
        isFirstFilterRun.current = true;

        const doSiteChange = async () => {
            setInitialLoading(true);
            setDocuments([]);  // ← FIX 1: now safe — skeleton is already showing, DataGrid is unmounted
            setTotalRows(0);   // ← FIX 1: clear stale count

            // Still null on an active switch — moving site deliberately resets the machine
            // filter — but otherwise the shared resolution, so admin view keeps its machine.
            const machineIdForSiteChange = isActiveSiteSwitch ? null : machineIdForApi;
            const categoryForSiteChange = isActiveSiteSwitch ? 'manual' : selectedCategory;
            await fetchDocuments(0, paginationModel.pageSize, categoryForSiteChange, machineIdForSiteChange, debouncedSearchTerm);
            await fetchFavorites();
            await fetchRecentlyViewed();
            setInitialLoadComplete(true);
            setInitialLoading(false);
            isSiteChangingRef.current = false; // ← FIX 2: re-enable filter effect after fetch done
        };

        doSiteChange();
    }, [effectiveSiteId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!selectedMachine && effectiveSiteId) {
            setSelectedMachine({
                machine_id: 'ALL',
                machine_name: '',
                machine_number: ''
            });
        }
    }, [effectiveSiteId]);

    // ─── Pagination change handler ────────────────────────────────────────────
    const handlePaginationModelChange = useCallback((newModel) => {
        setPaginationModel(newModel);

        // In favorites/recent mode, pagination is client-side only
        if (showOnlyFavorites || showOnlyRecent) return;

        fetchDocuments(
            newModel.page,
            newModel.pageSize,
            selectedCategory,
            machineIdForApi,
            debouncedSearchTerm
        );
    }, [
        fetchDocuments,
        selectedCategory,
        machineIdForApi,
        debouncedSearchTerm,
        showOnlyFavorites,
        showOnlyRecent
    ]);

    // ─── Admin all-sites favorites ────────────────────────────────────────────
    useEffect(() => {
        if (!isAdminView || !effectiveSiteId) return;

        const loadAdminAllSitesFavorites = async () => {
            try {
                const siteIds = [...new Set(
                    (documents || []).map(doc => doc.site_id).filter(id => id != null)
                )];
                if (siteIds.length === 0) { setAdminAllSitesFavoriteIds([]); return; }

                let allFavoriteDocs = [];
                for (const siteId of siteIds) {
                    try {
                        const response = await getFavorites(siteId);
                        if (response.status && Array.isArray(response.data)) {
                            allFavoriteDocs = allFavoriteDocs.concat(response.data);
                        }
                    } catch (err) {
                        console.error('Error fetching favorites for site:', siteId, err);
                    }
                }
                const uniqueIds = Array.from(new Set(allFavoriteDocs.map(doc => doc.id).filter(id => id != null)));
                setAdminAllSitesFavoriteIds(uniqueIds);
            } catch (err) {
                console.error('Error aggregating favorites for admin view:', err);
            }
        };

        loadAdminAllSitesFavorites();
    }, [isAdminView, effectiveSiteId, documents]);

    // ─── Relative time refresh interval ──────────────────────────────────────
    useEffect(() => {
        if (recentDocuments.length === 0) return;
        const interval = setInterval(() => {
            setRecentDocuments(prev => prev.map(doc => ({ ...doc, viewedTime: getRelativeTime(doc.viewed_at) })));
            setDocuments(prev => prev.map(doc =>
                doc.isRecentlyViewed && doc.viewed_at
                    ? { ...doc, viewedTime: getRelativeTime(doc.viewed_at) }
                    : doc
            ));
        }, 60000);
        return () => clearInterval(interval);
    }, [recentDocuments.length]);

    // ─── View param (recent / historical) — legacy ?view=recent support ──────
    useEffect(() => {
        if (viewParam === 'recent') {
            setShowOnlyRecent(true);
            setHighlightRecent(true);
            // Migrate legacy param to the new ?recent=true format
            setSearchParams(prev => {
                const next = new URLSearchParams(prev);
                next.delete('view');
                next.set('recent', 'true');
                return next;
            }, { replace: true });
            const timer = setTimeout(() => setHighlightRecent(false), 2000);
            return () => clearTimeout(timer);
        }
    }, [viewParam, setSearchParams]);

    useEffect(() => {
        if (location.state?.fromNotification && location.state?.machineNumber) {
            const machineNumberFromNotif = String(location.state.machineNumber);


            const machineByNumber = {
                machine_number: machineNumberFromNotif,
                machine_id: null,
                machine_name: ''
            };

            setSelectedMachine(machineByNumber);
            lastAppliedMachineRef.current = machineByNumber;
            setHighlightMachine(true);
            toast.trigger({ message: `Filtering documents for machine: ${machineNumberFromNotif}`, severity: 'success' });
            setTimeout(() => setHighlightMachine(false), 3000);
            window.history.replaceState({}, document.title);
        }
    }, [location.state, documents.length, selectedSite, selectedSiteId]);

    const machineFromNavStateRef = useRef(null);
    const setSearchParamsRef = useRef(setSearchParams);
    useEffect(() => {
        setSearchParamsRef.current = setSearchParams;
    }, [setSearchParams]);

    // ─── STEP 1: Read machine from navigation state or URL ───
    useEffect(() => {
        if (location.state?.fromNotification) {

            return;
        }

        const machineFromState = location.state?.machine;

        if (machineFromState?.machine_id || machineFromState?.machine_number) {
            const targetMachineId = String(machineFromState.machine_id || machineFromState.machine_number);
            const targetMachineNumber = String(machineFromState.machine_number || machineFromState.machine_id || '');


            // If nav state already provides a machine_id, we can skip delayed enrichment.
            if (!machineFromState?.machine_id) {
                machineFromNavStateRef.current = {
                    machine_number: targetMachineNumber,
                    machine_name: machineFromState.machine_name || ''
                };
            } else {
                machineFromNavStateRef.current = null;
            }

            initialMachineFromNavRef.current = targetMachineId;

            const nextMachine = {
                machine_id: targetMachineId,
                machine_name: machineFromState.machine_name || '',
                machine_number: targetMachineNumber
            };

            setSelectedMachine(nextMachine);
            lastAppliedMachineRef.current = nextMachine;

            // Use ref instead of setSearchParams directly
            setSearchParamsRef.current(prev => {
                const newParams = new URLSearchParams(prev);
                newParams.set('machine', targetMachineNumber || targetMachineId);
                return newParams;
            }, { replace: true });

            setHighlightMachine(true);
            const timer = setTimeout(() => setHighlightMachine(false), 2000);
            return () => clearTimeout(timer);
        }

        // PRIORITY 2: Machine in URL params
        const machineFromUrl = new URLSearchParams(location.search).get('machine');
        if (machineFromUrl) {


            const already = lastAppliedMachineRef.current;
            if (
                already &&
                (
                    String(already.machine_id) === String(machineFromUrl) ||
                    String(already.machine_number) === String(machineFromUrl)
                ) &&
                already.machine_name
            ) {

                initialMachineFromNavRef.current = already.machine_id;
                return;
            }

            setSelectedMachine({
                machine_id: machineFromUrl,
                machine_name: '',
                machine_number: machineFromUrl,
            });
            initialMachineFromNavRef.current = machineFromUrl;
            return;
        }

        // PRIORITY 3: Default to ALL
        if (!lastAppliedMachineRef.current) {
            sessionStorage.removeItem('selectedMachine');
            setSelectedMachine({
                machine_id: 'ALL',
                machine_name: '',
                machine_number: ''
            });
        }
    }, [location.pathname, location.state]);

    // ─── STEP 2: Once documents load, find correct machine_id for the machine_number ───
    useEffect(() => {
        if (!documents.length || !machineFromNavStateRef.current) {

            return;
        }

        const cachedMachine = machineFromNavStateRef.current;
        const targetMachineNumber = String(cachedMachine.machine_number);



        let foundCorrectMachine = null;

        for (const doc of documents) {
            if (!doc.machine_details) continue;
            const machineDetail = doc.machine_details.find(
                m => String(m.machine_number) === targetMachineNumber
            );
            if (machineDetail) {
                foundCorrectMachine = {
                    machine_id: machineDetail.machine_id,
                    machine_name: machineDetail.machine_name || cachedMachine.machine_name || '',
                    machine_number: machineDetail.machine_number || targetMachineNumber
                };

                break;
            }
        }

        if (foundCorrectMachine) {
            setSelectedMachine(foundCorrectMachine);
            lastAppliedMachineRef.current = foundCorrectMachine;
            initialMachineFromNavRef.current = foundCorrectMachine.machine_id;
            const currentUrlMachine = searchParams.get('machine');
            const correctUrlValue = String(foundCorrectMachine.machine_number || foundCorrectMachine.machine_id);

            if (String(currentUrlMachine) !== correctUrlValue) {

                setSearchParams(prev => {
                    const newParams = new URLSearchParams(prev);
                    newParams.set('machine', correctUrlValue);
                    return newParams;
                }, { replace: true });
            } else {

            }
        } else {

        }

        machineFromNavStateRef.current = null;
    }, [documents, searchParams, setSearchParams]);

    useEffect(() => {
        if (showOnlyRecent) fetchRecentlyViewed();
    }, [showOnlyRecent, fetchRecentlyViewed]);

    useEffect(() => {
        setSelectedDocuments([]);
    }, [selectedCategory]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
    }, []);

    const clearMachineFilter = useCallback(() => {
        setSelectedMachine({
            machine_id: 'ALL',
            machine_name: '',
            machine_number: ''
        });
        lastAppliedMachineRef.current = null;
        setHighlightMachine(false);
        if (isFromMachineCard) setIsFromMachineCard(false);
    }, [isFromMachineCard]);

    const handleMachineChange = useCallback((machine) => {
        setSelectedMachine(machine);
        lastAppliedMachineRef.current = machine;
        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            if (machine?.machine_id && machine.machine_id !== 'ALL') {
                const valueToSync = machine.machine_number
                    ? String(machine.machine_number)
                    : String(machine.machine_id);
                newParams.set('machine', valueToSync);
            } else {
                newParams.delete('machine');
            }
            return newParams;
        });

        if (machine?.machine_id !== initialMachineFromNavRef.current) {
            setHighlightMachine(false);
        }
        if (isFromMachineCard && machine?.machine_id !== initialMachineFromNavRef.current) {
            setIsFromMachineCard(false);
        }
    }, [isFromMachineCard, setSearchParams]);

    const handleToggleFavorites = useCallback((onlyFavorites) => {
        setShowOnlyFavorites(onlyFavorites);
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (onlyFavorites) next.set('favorites', 'true');
            else next.delete('favorites');
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    const handleToggleRecent = useCallback((onlyRecent) => {
        setShowOnlyRecent(onlyRecent);
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (onlyRecent) next.set('recent', 'true');
            else { next.delete('recent'); next.delete('view'); }
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    const handleFavoriteRemoved = useCallback((docId) => {

        setFavoriteDocumentsWithTimestamps(prev => prev.filter(doc => doc.id !== docId));
        setFavorites(prev => prev.filter(id => id !== docId));
        setDocuments(prev => prev.map(doc => doc.id === docId ? { ...doc, isFavorite: false } : doc));
    }, []);

    // ─── Date search helpers ──────────────────────────────────────────────────
    const parseSearchDate = (searchStr) => {
        if (!searchStr) return null;
        const mmddyyyy = searchStr.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
        if (mmddyyyy) return { month: mmddyyyy[1].padStart(2, '0'), day: mmddyyyy[2].padStart(2, '0'), year: mmddyyyy[3] };
        const mmddyyyySlash = searchStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (mmddyyyySlash) return { month: mmddyyyySlash[1].padStart(2, '0'), day: mmddyyyySlash[2].padStart(2, '0'), year: mmddyyyySlash[3] };
        const mmdd = searchStr.match(/^(\d{1,2})[-\/](\d{1,2})$/);
        if (mmdd) return { month: mmdd[1].padStart(2, '0'), day: mmdd[2].padStart(2, '0'), year: null };
        const yyyy = searchStr.match(/^(\d{4})$/);
        if (yyyy) return { month: null, day: null, year: yyyy[1] };
        return null;
    };

    const matchesDateSearch = (dateString, searchDate) => {
        if (!dateString || !searchDate) return false;
        try {
            const docDate = new Date(dateString);
            const docMonth = String(docDate.getMonth() + 1).padStart(2, '0');
            const docDay = String(docDate.getDate()).padStart(2, '0');
            const docYear = String(docDate.getFullYear());
            if (searchDate.year && searchDate.year !== docYear) return false;
            if (searchDate.month && searchDate.month !== docMonth) return false;
            if (searchDate.day && searchDate.day !== docDay) return false;
            return true;
        } catch { return false; }
    };

    const viewFavoriteIds = useMemo(() => {
        if (isAdminView && !effectiveSiteId) return adminAllSitesFavoriteIds;
        return favorites;
    }, [isAdminView, effectiveSiteId, adminAllSitesFavoriteIds, favorites]);

    const isDocumentInOthersCategory = useCallback((doc) => {
        const hasCategories = doc.categories && doc.categories.length > 0;
        const hasKnownCategory = hasCategories && doc.categories.some(cat =>
            KNOWN_DOCUMENT_CATEGORIES.includes(normalizeDocumentCategoryKey(cat.category_name))
        );
        return !hasCategories || !hasKnownCategory;
    }, []);

    // ─── Client-side filtering (favorites / recent only — category/search/machine are server-side) ──
    const filteredDocuments = useMemo(() => {
        const normalizedSearch = searchTerm.toLowerCase().trim();
        const searchDate = parseSearchDate(searchTerm.trim());

        const formatDateForSearch = (dateString) => {
            if (!dateString) return '';
            try {
                const date = new Date(dateString);
                return `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}-${date.getFullYear()}`;
            } catch { return ''; }
        };

        const normalizeForSearch = (str) => {
            if (!str) return '';
            return str.toLowerCase().replace(/[~\-_\s]/g, '');
        };

        const searchNormalized = normalizeForSearch(normalizedSearch);

        const recentById = new Map((recentDocuments || []).map(d => [d.id, d]));
        const documentsById = new Map((documents || []).map(d => [d.id, d]));

        const mergedFavorites = (favoriteDocumentsWithTimestamps || []).map((fav) => {
            const fromAll = documentsById.get(fav.id) || {};
            const recent = recentById.get(fav.id);
            return {
                ...fromAll,
                ...fav,
                isRecentlyViewed: Boolean(recent || fromAll.isRecentlyViewed),
                viewedTime: recent?.viewedTime ?? fromAll.viewedTime,
                viewed_at: recent?.viewed_at ?? fromAll.viewed_at,
                view_count: recent?.view_count ?? fromAll.view_count,
            };
        });

        const baseDocuments =
            showOnlyFavorites
                ? mergedFavorites
                : showOnlyRecent
                    ? recentDocuments
                    : documents;

        return baseDocuments.filter((doc) => {
            if (adminLocation && String(doc.site_id) !== String(adminLocation)) return false;
            if (adminMachine !== 'all' && !doc.assigned_machines?.includes(String(adminMachine))) return false;

            const selectedMachineId = selectedMachine?.machine_id;
            const hasMachineFilter =
                selectedMachineId != null &&
                String(selectedMachineId).toUpperCase() !== 'ALL';

            if ((showOnlyFavorites || showOnlyRecent) && hasMachineFilter && !isAdminView) {
                const machineIdStr = String(selectedMachineId);
                const assigned = (doc.assigned_machines || []).map(String);
                const matchesAssigned = assigned.includes(machineIdStr);
                const matchesDetails =
                    Array.isArray(doc.machine_details) &&
                    doc.machine_details.some(m => String(m.machine_id) === machineIdStr);

                if (!matchesAssigned && !matchesDetails) return false;
            }

            // Favorites filter (client-side only)
            if (showOnlyFavorites && !viewFavoriteIds.includes(doc.id)) return false;

            // Recent filter (client-side only)
            if (showOnlyRecent && !doc.isRecentlyViewed) return false;

            // In favorites/recent mode there is no server fetch for search, so client-side
            // filtering must always apply. In normal mode the server handles search after
            // the debounce settles — we do NOT apply a client-side preview while typing
            // because it causes a visible "flash" of partially-filtered results.
            const shouldApplyClientSideSearch =
                normalizedSearch &&
                (showOnlyFavorites || showOnlyRecent);

            if (shouldApplyClientSideSearch) {
                const isOtherDocument = isDocumentInOthersCategory(doc);
                const matchesSearch =
                    (doc.name || '').toLowerCase().includes(normalizedSearch) ||
                    (doc.description || '').toLowerCase().includes(normalizedSearch) ||
                    (isOtherDocument && normalizeForSearch(doc.document_type).includes(searchNormalized)) ||
                    // MERGED from old: also match against doc.category and categories type/document_type
                    normalizeForSearch(doc.category).includes(searchNormalized) ||
                    doc.machine_details?.some(m =>
                        (m.machine_name || '').toLowerCase().includes(normalizedSearch) ||
                        (m.machine_number || '').toLowerCase().includes(normalizedSearch)
                    ) ||
                    doc.categories?.some(cat =>
                        (cat.category_name || '').toLowerCase().includes(normalizedSearch) ||
                        (isOtherDocument && (
                            normalizeForSearch(cat.type).includes(searchNormalized) ||
                            normalizeForSearch(cat.document_type).includes(searchNormalized)
                        ))
                    ) ||
                    formatDateForSearch(doc.uploaded_at).includes(normalizedSearch) ||
                    formatDateForSearch(doc.updated_at).includes(normalizedSearch) ||
                    (searchDate && (matchesDateSearch(doc.uploaded_at, searchDate) || matchesDateSearch(doc.updated_at, searchDate)));
                if (!matchesSearch) return false;
            }

            return true;
        });
    }, [
        documents,
        searchTerm,
        debouncedSearchTerm,
        showOnlyFavorites,
        showOnlyRecent,
        viewFavoriteIds,
        adminLocation,
        adminMachine,
        selectedMachine?.machine_id,
        isAdminView,
        selectedCategory,
        isDocumentInOthersCategory,
        favoriteDocumentsWithTimestamps,
        recentDocuments,
    ]);

    const sortedFilteredDocuments = useMemo(() => {
        let sorted = [...filteredDocuments];

        // Only apply client-side category filtering in favorites/recent mode
        if ((showOnlyFavorites || showOnlyRecent) && selectedCategory) {
            sorted = sorted.filter(doc => {

                // Special case for Others
                if (selectedCategory === 'others') {
                    if (showOnlyFavorites) {
                        // Move RSPL into Others in favorites mode
                        const hasRspl = doc.categories?.some(
                            cat => normalizeDocumentCategoryKey(cat.category_name) === 'others'
                        );
                        return isDocumentInOthersCategory(doc) || hasRspl;
                    }

                    return isDocumentInOthersCategory(doc);
                }

                return doc.categories?.some(
                    cat => normalizeDocumentCategoryKey(cat.category_name) === selectedCategory
                );
            });
        }

        // Sorting only (no normal-mode filtering)
        if (sortOption === t('DOCUMENTATION.SORT_NONE')) return sorted;

        switch (sortOption) {
            case t('DOCUMENTATION.SORT_FILE_NAME_ASC'):
                return sorted.sort((a, b) => a.name.localeCompare(b.name));
            case t('DOCUMENTATION.SORT_FILE_NAME_DESC'):
                return sorted.sort((a, b) => b.name.localeCompare(a.name));
            case t('DOCUMENTATION.SORT_SERIAL_NUMBER_ASC'):
                return sorted.sort((a, b) => (parseInt(a.id) || 0) - (parseInt(b.id) || 0));
            case t('DOCUMENTATION.SORT_SERIAL_NUMBER_DESC'):
                return sorted.sort((a, b) => (parseInt(b.id) || 0) - (parseInt(a.id) || 0));
            case t('DOCUMENTATION.SORT_DATE_ASC'):
                return sorted.sort((a, b) => new Date(a.uploaded_at || 0) - new Date(b.uploaded_at || 0));
            case t('DOCUMENTATION.SORT_DATE_DESC'):
                return sorted.sort((a, b) => new Date(b.uploaded_at || 0) - new Date(a.uploaded_at || 0));
            default:
                return sorted;
        }
    }, [
        filteredDocuments,
        selectedCategory,
        sortOption,
        t,
        showOnlyFavorites,
        showOnlyRecent,
        isDocumentInOthersCategory
    ]);

    useEffect(() => {
        if (!showOnlyFavorites && !showOnlyRecent) return;

        const trimmedSearchTerm = String(debouncedSearchTerm || "").trim();
        if (!trimmedSearchTerm) return;

        const resultCount = sortedFilteredDocuments.length;
        const searchSource = showOnlyFavorites
            ? "documentation_favorites"
            : "documentation_recent";

        const trackingKey = [
            trimmedSearchTerm.toLowerCase(),
            effectiveSiteId || "",
            selectedCategory || "",
            selectedMachine?.machine_id || "",
            searchSource,
            resultCount,
        ].join("|");

        if (lastTrackedSearchRef.current === trackingKey) return;

        lastTrackedSearchRef.current = trackingKey;

        trackEvent("search_performed", {
            search_term: trimmedSearchTerm,
            search_source: searchSource,
            page_name: "Documentation",
            page_path: `${location.pathname}${location.search || ""}`,
            result_count: resultCount,
            has_results: resultCount > 0,
            no_results: resultCount === 0,
            category: selectedCategory || "",
            machine_id: selectedMachine?.machine_id || "",
            site_id: effectiveSiteId || "",
            site_number: effectiveSiteNumber || "",
        });
    }, [
        debouncedSearchTerm,
        showOnlyFavorites,
        showOnlyRecent,
        sortedFilteredDocuments.length,
        effectiveSiteId,
        effectiveSiteNumber,
        selectedCategory,
        selectedMachine?.machine_id,
        location.pathname,
        location.search,
    ]);
    // AFTER — prefer machine_number over raw machine_id for display
    const getMachineDisplayName = (machine) => {
        if (machine?.machine_name) {
            return `${machine.machine_name}${machine.machine_number ? ` (${machine.machine_number})` : ''}`;
        }
        // Enrichment still pending — show a non-confusing placeholder
        return machine?.machine_number
            ? `Machine #${machine.machine_number}`
            : machine?.machine_id
                ? `Machine ID: ${machine.machine_id}`
                : '';
    };

    // ─── Category counts ──────────────────────────────────────────────────────
    const categoryCounts = useMemo(() => {
        const allCategories = [...DOCUMENT_TAB_CATEGORIES];
        const counts = {};
        allCategories.forEach(cat => { counts[cat] = { total: 0, matched: 0 }; });

        if (Object.keys(serverCategoryCounts).length > 0) {
            Object.entries(serverCategoryCounts).forEach(([category, count]) => {
                if (counts[category] !== undefined) {
                    counts[category] = { total: count, matched: count };
                }
            });
            return counts;
        }

        return counts;
    }, [serverCategoryCounts]);

    const hasAnyDocuments = useMemo(
        () => !initialLoading && Array.isArray(documents) && documents.length > 0,
        [initialLoading, documents]
    );

    const filteredCategoryCounts = useMemo(() => {
        const categories = [
            'manual',
            'boms',
            'mechanical-drawings',
            'electrical-drawings',
            'compliance',
            'food-compliance',
            'translations',
            'others'
        ];
        const counts = {};
        categories.forEach(cat => {
            counts[cat] = { total: 0, matched: 0 };
        });

        if (showOnlyFavorites || showOnlyRecent) {
            filteredDocuments.forEach(doc => {
                if (doc.categories?.length > 0) {
                    doc.categories.forEach(cat => {
                        const categoryName = normalizeDocumentCategoryKey(cat.category_name);
                        if (counts[categoryName]) {
                            counts[categoryName].total += 1;
                            counts[categoryName].matched += 1;
                        }
                    });
                } else {
                    counts['others'].total += 1;
                    counts['others'].matched += 1;
                }
            });
            return counts;
        }

        const isSearching = (debouncedSearchTerm || '').trim().length > 0;
        const countsAreStale = lastCountsKeyRef.current.searchTerm !== (debouncedSearchTerm || '').trim();

        if (countsLoading || countsAreStale) {
            // Seed every tab with the last-known counts so they all stay visible
            Object.entries(serverCategoryCounts).forEach(([category, count]) => {
                if (counts[category] !== undefined) {
                    counts[category] = { total: count, matched: count };
                }
            });
            // Live-update just the active tab with the freshest pagination total
            if (selectedCategory && totalRows > 0) {
                counts[selectedCategory] = { total: totalRows, matched: totalRows };
            }
            return counts;
        }

        Object.entries(serverCategoryCounts).forEach(([category, count]) => {
            if (counts[category]) {
                counts[category] = { total: count, matched: count };
            }
        });

        return Object.entries(counts).reduce((acc, [category, catCounts]) => {
            const count = isSearching ? catCounts.matched : catCounts.total;
            if (count > 0) {
                acc[category] = catCounts;
            }
            return acc;
        }, {});
    }, [
        filteredDocuments,
        serverCategoryCounts,
        showOnlyFavorites,
        showOnlyRecent,
        searchTerm,
        debouncedSearchTerm,
        countsLoading,
        totalRows,
        selectedCategory,
    ]);

    useEffect(() => {
        if (isSiteChangingRef.current) return;
        if (initialLoading || tableLoading) return;

        const currentCount = filteredCategoryCounts[selectedCategory]?.total || 0;
        if (currentCount > 0) return;

        const manualCount = filteredCategoryCounts?.manual?.total || 0;
        const highestCountTab = Object.entries(filteredCategoryCounts).reduce((best, [category, counts]) => {
            if ((counts?.total || 0) <= 0) return best;
            if (!best) return [category, counts];
            return (counts.total > best[1].total) ? [category, counts] : best;
        }, null);
        const preferredTab = manualCount > 0 ? 'manual' : highestCountTab?.[0];

        if (preferredTab && preferredTab !== selectedCategory) {
            // FIX: functional form preserves machine= and all other params
            setSearchParams(prev => {
                const next = new URLSearchParams(prev);
                next.set('category', preferredTab);
                return next;
            }, { replace: true });
        }
    }, [
        filteredCategoryCounts,
        selectedCategory,
        initialLoading,
        tableLoading,
        setSearchParams
    ]);

    useEffect(() => {
        if (isSiteChangingRef.current) return;
        if (!showOnlyFavorites) return;

        const currentTabCount = filteredCategoryCounts[selectedCategory]?.total || 0;
        if (currentTabCount > 0) return;

        const manualCount = filteredCategoryCounts?.manual?.total || 0;
        const highestCountTab = Object.entries(filteredCategoryCounts).reduce((best, [category, counts]) => {
            if ((counts?.total || 0) <= 0) return best;
            if (!best) return [category, counts];
            return (counts.total > best[1].total) ? [category, counts] : best;
        }, null);
        const preferredTab = manualCount > 0 ? 'manual' : highestCountTab?.[0];

        if (preferredTab) {
            // FIX: functional form preserves machine= and all other params
            setSearchParams(prev => {
                const next = new URLSearchParams(prev);
                next.set('category', preferredTab);
                return next;
            }, { replace: true, state: cloneSafeLocationState });
        }
    }, [showOnlyFavorites, selectedCategory, filteredCategoryCounts, setSearchParams, cloneSafeLocationState]);

    useEffect(() => { setIsManualTabSelection(false); }, [selectedMachine?.machine_id, showOnlyFavorites, showOnlyRecent, searchTerm]);

    // ─── Category change: updates URL (not state) ───────────────────────────
    const handleCategoryChange = useCallback((newCategory) => {
        setIsManualTabSelection(true);
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('category', newCategory);
            return next;
        }, { replace: true, state: cloneSafeLocationState });
    }, [setSearchParams, cloneSafeLocationState]);

    const handleDocumentsCategoryUpdated = useCallback((targetCategory) => {
        const targetTab = getCategoryTabKey(targetCategory);

        setSelectedDocuments([]);
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
        setRefreshNonce((prev) => prev + 1);

        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('category', targetTab);
            return next;
        }, { replace: true, state: cloneSafeLocationState });
    }, [getCategoryTabKey, cloneSafeLocationState, setSearchParams]);

    const handleToggleFavorite = useCallback(async (docId, isCurrentlyFavorite) => {

        if (isCurrentlyFavorite) {
            setFavorites(prev => prev.filter(id => id !== docId));
            if (isAdminView && !effectiveSiteId) setAdminAllSitesFavoriteIds(prev => prev.filter(id => id !== docId));
            setFavoriteDocumentsWithTimestamps(prev => prev.filter(doc => doc.id !== docId));
            setDocuments(prev => prev.map(doc => doc.id === docId ? { ...doc, isFavorite: false } : doc));
        } else {
            setFavorites(prev => [...prev, docId]);
            if (isAdminView && !effectiveSiteId) setAdminAllSitesFavoriteIds(prev => prev.includes(docId) ? prev : [...prev, docId]);
            // In "recently viewed" mode, the doc may not exist in `documents` (server-paginated list)
            // but will exist in `recentDocuments`. Use either so favorites sidebar updates immediately.
            const doc =
                documents.find(d => d.id === docId) ||
                recentDocuments.find(d => d.id === docId);
            if (doc) {
                const now = new Date().toISOString();
                setFavoriteDocumentsWithTimestamps(prev => [
                    { ...doc, favorited_at: now },
                    ...prev.filter(d => d.id !== docId),
                ]);
            }
            setDocuments(prev => prev.map(doc => doc.id === docId ? { ...doc, isFavorite: true } : doc));
        }
    }, [documents, recentDocuments, isAdminView, effectiveSiteId]);

    const handleFavoriteToggled = useCallback((viewedDoc) => {
        if (!viewedDoc) return;
        const now = new Date().toISOString();
        const updatedDoc = {
            ...viewedDoc,
            isRecentlyViewed: true,
            viewed_at: now,
            viewedTime: getRelativeTime(now)
        };
        setRecentDocuments(prev => {
            const filtered = prev.filter(d => d.id !== viewedDoc.id);
            return [updatedDoc, ...filtered];
        });
        setDocuments(prev =>
            prev.map(doc =>
                doc.id === viewedDoc.id
                    ? { ...updatedDoc }
                    : doc
            )
        );
    }, []);

    const hasActiveFilters =
        showOnlyFavorites ||
        showOnlyRecent ||
        (!!selectedMachine && selectedMachine.machine_id && selectedMachine.machine_id !== 'ALL');

    // ─── Search/sort change handlers ──────────────────────────────────────────
    const handleSearchChange = useCallback((newValue) => setSearchTerm(newValue), []);
    const handleSortChange = useCallback((newSortOption) => setSortOption(newSortOption), []);

    // ─── ONLY show full skeleton on very first load ───────────────────────────
    if (initialLoading) {
        return (
            <DataGridSkeletonForDocumentation
                showSidebar={!hideSidebar}
                showTabs={true}
                tabCount={7}
                showSearch={true}
                rows={10}
            />
        );
    }

    return (
        <div className="documentation-page">
            {!hideSidebar && <PageTitle title={t('DOCUMENTATION.TITLE')} />}

            <div className="documentation-content">
                {!hideSidebar && (
                    <div className="filters">
                        <div className="filter-content">
                            <DocumentationSidebar
                                selectedMachine={selectedMachine}
                                onMachineChange={handleMachineChange}
                                onToggleFavorites={handleToggleFavorites}
                                favoriteDocumentsWithTimestamps={favoriteDocumentsWithTimestamps}
                                onToggleRecent={handleToggleRecent}
                                favorites={viewFavoriteIds}
                                recentDocuments={recentDocuments}
                                documents={documents}
                                showOnlyFavorites={showOnlyFavorites}
                                showOnlyRecent={showOnlyRecent}
                                selectedSiteNumber={effectiveSiteNumber}
                                selectedSiteId={effectiveSiteId}
                                highlightRecent={highlightRecent}
                                highlightMachine={highlightMachine}
                                onFavoriteRemoved={handleFavoriteRemoved}
                            />
                        </div>
                    </div>
                )}

                {!hideSidebar && <Divider flexItem orientation="vertical" sx={{ my: 0.5 }} />}

                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, width: '100%', overflow: 'auto' }}>
                    {error && (
                        <Alert severity="error" className="error-alert" onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    {/* Always render the UI shell — never unmount on filter changes */}
                    <>
                        <Box sx={{ mb: 1 }}>
                            <ContextFilterAndSort
                                searchValue={searchTerm}
                                onSearchChange={handleSearchChange}
                                placeholder={t('DOCUMENTATION.SEARCH_PLACEHOLDER')}
                                options={sortOptions}
                                selectedOption={sortOption}
                                onSortChange={handleSortChange}
                                showSearch={true}
                                showSort={false}
                            />
                        </Box>

                        <Box sx={{ mb: 0 }}>
                            <DocumentationTabs
                                selectedCategory={selectedCategory}
                                onCategoryChange={handleCategoryChange}
                                categoryCounts={filteredCategoryCounts}
                                searchTerm={debouncedSearchTerm}
                                fromMachineCard={isFromMachineCard}
                            />
                        </Box>

                        {hasActiveFilters && (
                            <Box className="active-filters-container">
                                <Typography variant="body2" className="filter-label">
                                    {t('DOCUMENTATION.ACTIVE_FILTERS')}
                                </Typography>
                                {selectedMachine && selectedMachine.machine_id !== 'ALL' && (
                                    <Chip
                                        label={getMachineDisplayName(selectedMachine)}
                                        size="small"
                                        onDelete={clearMachineFilter}
                                        deleteIcon={<CloseIcon />}
                                        className="filter-chip"
                                    />
                                )}
                                {showOnlyFavorites && (
                                    <Chip
                                        label={t('DOCUMENTATION.FAVORITES_FILTER')}
                                        size="small"
                                        onDelete={() => handleToggleFavorites(false)}
                                        deleteIcon={<CloseIcon />}
                                        className="filter-chip"
                                    />
                                )}
                                {showOnlyRecent && (
                                    <Chip
                                        label={t('DOCUMENTATION.RECENTLY_VIEWED_FILTER')}
                                        size="small"
                                        onDelete={() => handleToggleRecent(false)}
                                        deleteIcon={<CloseIcon />}
                                        className="filter-chip"
                                    />
                                )}
                            </Box>
                        )}

                        {/* Table area: show spinner overlay instead of re-mounting */}
                        <Box sx={{ position: 'relative' }}>
                            {!initialLoading && initialLoadComplete && sortedFilteredDocuments.length === 0 && !tableLoading ? (
                                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', width: '100%', px: 2, py: 8 }}>
                                    <Box sx={{ maxWidth: 600 }}>
                                        <Typography variant="h6" color="text.secondary" fontWeight={500}>
                                            {t('DOCUMENTATION.NO_DOCUMENTS_AVAILABLE')}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '500px', mx: 'auto', mt: 1 }}>
                                            {t('DOCUMENTATION.NO_DOCUMENTS_DESCRIPTION')}
                                        </Typography>
                                    </Box>
                                </Box>
                            ) : (
                                <DocumentationTable
                                    documents={sortedFilteredDocuments}
                                    favorites={viewFavoriteIds}
                                    selectedDocuments={selectedDocuments}
                                    onToggleFavorite={handleToggleFavorite}
                                    onSelectDocument={setSelectedDocuments}
                                    onFavoriteToggled={handleFavoriteToggled}
                                    loading={tableLoading}
                                    selectedSiteId={effectiveSiteId}
                                    currentCategory={selectedCategory}
                                    userRole={user?.role}
                                    canAddDocument={canAddDocument}
                                    canUpdateCategory={canUpdateDocumentCategories}
                                    canChangeMachine={canChangeDocumentMachine}
                                    canDeleteDocuments={canDeleteDocuments}
                                    canDownloadSelectedDocuments={canDownloadSelectedDocuments}
                                    onCategoriesUpdated={handleDocumentsCategoryUpdated}
                                    hideFavoriteIcon={hideSidebar}
                                    rowCount={(showOnlyFavorites || showOnlyRecent) ? sortedFilteredDocuments.length : totalRows}
                                    paginationModel={paginationModel}
                                    onPaginationModelChange={handlePaginationModelChange}
                                    paginationMode={(showOnlyFavorites || showOnlyRecent) ? "client" : "server"}
                                />
                            )}
                        </Box>
                    </>
                </Box>
            </div>
        </div>
    );
};

export default Documentation;
